import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';

// Offline-first data layer
import { saveDataEntry, FORM_TYPES } from 'utils/offlineFirstDataLayer';

// material-ui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, startOfDay } from 'date-fns';

// ant design icons
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CalendarOutlined from '@ant-design/icons/CalendarOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import FileSearchOutlined from '@ant-design/icons/FileSearchOutlined';

// project imports
import MainCard from 'components/MainCard';


const ParentTrainingMode = ({
  viewMode = 'individual',
  onDataChange,
  childId,
  childName = '',
  therapistId,
  therapistName = '',
  sessionId = null,
}) => {
  const theme = useTheme();
  const intl = useIntl();

  // Saving state to prevent duplicate saves
  const [saving, setSaving] = useState(false);

  // Form state
  const [observations, setObservations] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [newObservation, setNewObservation] = useState({
    date: new Date(),
    duration: '',
    parentPresent: '',
    programsObserved: '',
    parentGoals: '',
    parentConcerns: ''
  });

  // Ref to skip auto-save on initial render
  const initialRenderRef = useRef(true);

  // Auto-save: notify parent when form data changes
  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      return;
    }

    // Skip if no meaningful data has been entered
    const hasObservations = observations.length > 0;
    const hasFormData = newObservation.duration || newObservation.parentPresent ||
      newObservation.programsObserved || newObservation.parentGoals || newObservation.parentConcerns;

    if (!hasObservations && !hasFormData) return;

    const formData = {
      observations: observations.map((obs) => ({
        id: obs.id,
        date: obs.date,
        duration: obs.duration,
        parentPresent: obs.parentPresent,
        programsObserved: obs.programsObserved,
        parentGoals: obs.parentGoals,
        parentConcerns: obs.parentConcerns
      })),
      currentForm: showAddForm ? { ...newObservation } : null,
      editingRecordId: editingRecord?.id || null,
      metadata: {
        childId,
        childName,
        therapistId,
        therapistName,
        sessionId,
        totalRecords: observations.length
      }
    };

    if (onDataChange) onDataChange('parent-training', formData);
  }, [observations, newObservation, showAddForm, editingRecord]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sort helper function
  const sortByDate = (recordsArray) => {
    return recordsArray.sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA;
    });
  };

  // Filter today's records
  const todaysRecords = observations.filter((record) => {
    const recordDate = startOfDay(record.date);
    const today = startOfDay(new Date());
    return recordDate.getTime() === today.getTime();
  });

  const handleAddObservation = async () => {
    if (saving) {
      return;
    }

    if (!newObservation.parentPresent || !newObservation.programsObserved) {
      return;
    }

    setSaving(true);

    try {
      const record = {
        id: editingRecord?.id || Date.now(),
        ...newObservation
      };

      // Save to IndexedDB for offline-first support
      await saveDataEntry(
        FORM_TYPES.PARENT_TRAINING,
        record,
        {
          childId,
          childName,
          therapistId,
          therapistName,
          sessionId,
          sessionDate: new Date().toISOString()
        }
      );

      if (editingRecord) {
        // Update existing record
        setObservations((prev) =>
          sortByDate(
            prev.map((obs) =>
              obs.id === editingRecord.id ? record : obs
            )
          )
        );
        setEditingRecord(null);
      } else {
        // Add new record
        setObservations((prev) => sortByDate([record, ...prev]));
      }

      setNewObservation({
        date: new Date(),
        duration: '',
        parentPresent: '',
        programsObserved: '',
        parentGoals: '',
        parentConcerns: ''
      });
      setShowAddForm(false);

      if (onDataChange) onDataChange();
    } catch (error) {
      console.error('[ParentTrainingMode] Failed to save observation:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);
    setNewObservation({
      date: record.date || new Date(),
      duration: record.duration || '',
      parentPresent: record.parentPresent || '',
      programsObserved: record.programsObserved || '',
      parentGoals: record.parentGoals || '',
      parentConcerns: record.parentConcerns || ''
    });
    setShowAddForm(true);
  };

  const handleDeleteRecord = (id) => {
    setObservations((prev) => prev.filter((obs) => obs.id !== id));
    if (onDataChange) onDataChange();
  };

  const handleCancelForm = () => {
    setShowAddForm(false);
    setEditingRecord(null);
    setNewObservation({
      date: new Date(),
      duration: '',
      parentPresent: '',
      programsObserved: '',
      parentGoals: '',
      parentConcerns: ''
    });
  };

  // Grid view inline edit handler
  const handleGridFieldChange = (id, field, value) => {
    setObservations((prev) =>
      prev.map((obs) => (obs.id === id ? { ...obs, [field]: value } : obs))
    );
    if (onDataChange) onDataChange();
  };

  // Add new row for grid view
  const handleAddGridRow = () => {
    const newRow = {
      id: Date.now(),
      date: new Date(),
      duration: '',
      parentPresent: '',
      programsObserved: '',
      parentGoals: '',
      parentConcerns: ''
    };
    setObservations((prev) => sortByDate([newRow, ...prev]));
    if (onDataChange) onDataChange();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Observation Records */}
        {viewMode === 'individual' ? (
          // Individual Card View - ABC Analysis Style
          <MainCard>
            <Stack spacing={2}>
              {/* Header with New Record Button */}
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h5">{intl.formatMessage({ id: 'form.parentTraining.observationRecords' }, { count: observations.length })}</Typography>
                {!showAddForm && (
                  <Button variant="contained" startIcon={<PlusOutlined />} onClick={() => setShowAddForm(true)}>
                    {intl.formatMessage({ id: 'form.parentTraining.new' })}
                  </Button>
                )}
              </Stack>

              {/* Add/Edit Form */}
              {showAddForm && (
                <Paper sx={{ p: 3, border: 1, borderColor: 'divider', bgcolor: 'grey.50', '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } }}>
                  <Stack spacing={3}>
                    {/* Date, Duration & Parent Present */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <DatePicker
                          label={intl.formatMessage({ id: 'form.label.date' })}
                          value={newObservation.date}
                          onChange={(newValue) => {
                            setNewObservation({ ...newObservation, date: newValue });
                            if (onDataChange) onDataChange();
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label={intl.formatMessage({ id: 'form.parentTraining.duration' })}
                          value={newObservation.duration}
                          onChange={(e) => {
                            setNewObservation({ ...newObservation, duration: e.target.value });
                            if (onDataChange) onDataChange();
                          }}
                          placeholder={intl.formatMessage({ id: 'form.parentTraining.durationPlaceholder' })}
                          InputProps={{
                            startAdornment: <ClockCircleOutlined style={{ marginRight: 8, color: theme.palette.text.secondary }} />
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label={intl.formatMessage({ id: 'form.parentTraining.parentPresent' })}
                          value={newObservation.parentPresent}
                          onChange={(e) => {
                            setNewObservation({ ...newObservation, parentPresent: e.target.value });
                            if (onDataChange) onDataChange();
                          }}
                          placeholder={intl.formatMessage({ id: 'form.parentTraining.parentPresentPlaceholder' })}
                        />
                      </Grid>
                    </Grid>

                    {/* Programs Observed */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'primary.main' }}>
                        {intl.formatMessage({ id: 'form.parentTraining.programsObserved' })}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={newObservation.programsObserved}
                        onChange={(e) => {
                          setNewObservation({ ...newObservation, programsObserved: e.target.value });
                          if (onDataChange) onDataChange();
                        }}
                        placeholder={intl.formatMessage({ id: 'form.parentTraining.programsObservedPlaceholder' })}
                      />
                    </Box>

                    {/* Parent Goals */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'success.main' }}>
                        {intl.formatMessage({ id: 'form.parentTraining.parentGoals' })}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={newObservation.parentGoals}
                        onChange={(e) => {
                          setNewObservation({ ...newObservation, parentGoals: e.target.value });
                          if (onDataChange) onDataChange();
                        }}
                        placeholder={intl.formatMessage({ id: 'form.parentTraining.parentGoalsPlaceholder' })}
                      />
                    </Box>

                    {/* Parent Concerns */}
                    <Box>
                      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'warning.main' }}>
                        {intl.formatMessage({ id: 'form.parentTraining.parentConcerns' })}
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={newObservation.parentConcerns}
                        onChange={(e) => {
                          setNewObservation({ ...newObservation, parentConcerns: e.target.value });
                          if (onDataChange) onDataChange();
                        }}
                        placeholder={intl.formatMessage({ id: 'form.parentTraining.parentConcernsPlaceholder' })}
                      />
                    </Box>

                    {/* Actions */}
                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                      <Button variant="outlined" onClick={handleCancelForm}>
                        {intl.formatMessage({ id: 'common.cancel' })}
                      </Button>
                      {editingRecord && (
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlined />}
                          onClick={() => {
                            handleDeleteRecord(editingRecord.id);
                            handleCancelForm();
                          }}
                        >
                          {intl.formatMessage({ id: 'common.delete' })}
                        </Button>
                      )}
                      <Button
                        variant="contained"
                        startIcon={<CheckOutlined />}
                        onClick={handleAddObservation}
                        disabled={!newObservation.parentPresent || !newObservation.programsObserved || saving}
                      >
                        {saving ? intl.formatMessage({ id: 'common.saving' }) : (editingRecord ? intl.formatMessage({ id: 'form.parentTraining.update' }) : intl.formatMessage({ id: 'form.parentTraining.save' }))}
                      </Button>
                    </Stack>
                  </Stack>
                </Paper>
              )}

              {/* Records List */}
              {!showAddForm && observations.length === 0 ? (
                <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                  <FileSearchOutlined style={{ fontSize: '3rem', color: theme.palette.grey[300] }} />
                  <Typography variant="h6" color="text.secondary">
                    {intl.formatMessage({ id: 'form.parentTraining.noRecordsYet' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {intl.formatMessage({ id: 'form.parentTraining.clickNew' })}
                  </Typography>
                </Stack>
              ) : !showAddForm ? (
                <Stack spacing={2}>
                  {observations.map((obs) => (
                    <Paper
                      key={obs.id}
                      elevation={0}
                      sx={{
                        p: 2.5,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 2,
                        '&:hover': {
                          boxShadow: theme.shadows[2]
                        }
                      }}
                    >
                      <Stack spacing={2}>
                        {/* Header */}
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                            <CalendarOutlined style={{ fontSize: '1.2rem', color: theme.palette.text.secondary }} />
                            <Typography variant="subtitle2" fontWeight={600}>
                              {obs.date ? format(obs.date, 'MMM dd, yyyy') : intl.formatMessage({ id: 'form.toileting.noDate' })}
                            </Typography>
                            {obs.duration && (
                              <Chip
                                icon={<ClockCircleOutlined />}
                                label={obs.duration}
                                size="small"
                                variant="outlined"
                              />
                            )}
                            {obs.parentPresent && (
                              <Chip
                                icon={<UserOutlined />}
                                label={obs.parentPresent}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <IconButton size="small" color="primary" onClick={() => handleEditRecord(obs)}>
                              <EditOutlined />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDeleteRecord(obs.id)}>
                              <DeleteOutlined />
                            </IconButton>
                          </Stack>
                        </Stack>

                        {/* Content Cards */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* Row 1: Programs Observed & Parent Goals - 50/50 */}
                          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                flex: 1,
                                bgcolor: alpha(theme.palette.primary.main, 0.08),
                                borderRadius: 1.5,
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
                              }}
                            >
                              <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ mb: 0.5, display: 'block' }}>
                                {intl.formatMessage({ id: 'form.parentTraining.programsObservedSection' })}
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                {obs.programsObserved || '-'}
                              </Typography>
                            </Paper>

                            <Paper
                              elevation={0}
                              sx={{
                                p: 2,
                                flex: 1,
                                bgcolor: alpha(theme.palette.success.main, 0.08),
                                borderRadius: 1.5,
                                border: `1px solid ${alpha(theme.palette.success.main, 0.12)}`
                              }}
                            >
                              <Typography variant="caption" fontWeight={700} color="success.main" sx={{ mb: 0.5, display: 'block' }}>
                                {intl.formatMessage({ id: 'form.parentTraining.parentGoalsSection' })}
                              </Typography>
                              <Typography variant="body2" color="text.primary">
                                {obs.parentGoals || '-'}
                              </Typography>
                            </Paper>
                          </Box>

                          {/* Row 2: Parent Concerns - Full Width */}
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              bgcolor: alpha(theme.palette.warning.main, 0.08),
                              borderRadius: 1.5,
                              border: `1px solid ${alpha(theme.palette.warning.main, 0.12)}`
                            }}
                          >
                            <Typography variant="caption" fontWeight={700} color="warning.dark" sx={{ mb: 0.5, display: 'block' }}>
                              {intl.formatMessage({ id: 'form.parentTraining.parentConcernsSection' })}
                            </Typography>
                            <Typography variant="body2" color="text.primary">
                              {obs.parentConcerns || '-'}
                            </Typography>
                          </Paper>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : null}
            </Stack>
          </MainCard>
        ) : (
          // Grid/Table View
          <MainCard
            title={intl.formatMessage({ id: 'form.parentTraining.observationRecordsTitle' })}
            secondary={
              <Button
                variant="contained"
                size="small"
                startIcon={<PlusOutlined />}
                onClick={handleAddGridRow}
              >
                {intl.formatMessage({ id: 'form.toileting.addRecord' })}
              </Button>
            }
          >
            <TableContainer component={Paper} elevation={0}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                    <TableCell sx={{ fontWeight: 600, width: '20%' }}>
                      {intl.formatMessage({ id: 'form.parentTraining.dateDurationParent' })}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '25%' }}>{intl.formatMessage({ id: 'form.parentTraining.programsObserved' })}</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '25%' }}>{intl.formatMessage({ id: 'form.parentTraining.parentGoals' })}</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '25%' }}>{intl.formatMessage({ id: 'form.parentTraining.parentConcerns' })}</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '5%' }}>{intl.formatMessage({ id: 'form.parentTraining.actions' })}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {observations.map((obs) => (
                    <TableRow
                      key={obs.id}
                      hover
                      sx={{
                        '&:hover .delete-btn': {
                          opacity: 1
                        }
                      }}
                    >
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Stack spacing={1.5}>
                          <DatePicker
                            label={intl.formatMessage({ id: 'form.label.date' })}
                            value={obs.date}
                            onChange={(newValue) => handleGridFieldChange(obs.id, 'date', newValue)}
                            slotProps={{
                              textField: {
                                size: 'small',
                                fullWidth: true
                              }
                            }}
                          />
                          <TextField
                            size="small"
                            label={intl.formatMessage({ id: 'form.parentTraining.duration' })}
                            value={obs.duration}
                            onChange={(e) => handleGridFieldChange(obs.id, 'duration', e.target.value)}
                            placeholder={intl.formatMessage({ id: 'form.parentTraining.durationPlaceholder' })}
                            fullWidth
                          />
                          <TextField
                            size="small"
                            label={intl.formatMessage({ id: 'form.parentTraining.parentPresent' })}
                            value={obs.parentPresent}
                            onChange={(e) => handleGridFieldChange(obs.id, 'parentPresent', e.target.value)}
                            placeholder={intl.formatMessage({ id: 'form.parentTraining.parentPresentShort' })}
                            fullWidth
                          />
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          rows={6}
                          value={obs.programsObserved}
                          onChange={(e) => handleGridFieldChange(obs.id, 'programsObserved', e.target.value)}
                          placeholder={intl.formatMessage({ id: 'form.parentTraining.programsObservedShort' })}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          rows={6}
                          value={obs.parentGoals}
                          onChange={(e) => handleGridFieldChange(obs.id, 'parentGoals', e.target.value)}
                          placeholder={intl.formatMessage({ id: 'form.parentTraining.parentGoalsShort' })}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <TextField
                          size="small"
                          fullWidth
                          multiline
                          rows={6}
                          value={obs.parentConcerns}
                          onChange={(e) => handleGridFieldChange(obs.id, 'parentConcerns', e.target.value)}
                          placeholder={intl.formatMessage({ id: 'form.parentTraining.parentConcernsShort' })}
                        />
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteRecord(obs.id)}
                          className="delete-btn"
                          sx={{ opacity: 0.7 }}
                        >
                          <DeleteOutlined />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {observations.length === 0 && (
              <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                <FileSearchOutlined style={{ fontSize: '3rem', color: theme.palette.grey[300] }} />
                <Typography variant="h6" color="text.secondary">
                  {intl.formatMessage({ id: 'form.parentTraining.noRecordsYet' })}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {intl.formatMessage({ id: 'form.parentTraining.clickAddRecord' })}
                </Typography>
              </Stack>
            )}
          </MainCard>
        )}
      </Box>
    </LocalizationProvider>
  );
};

ParentTrainingMode.propTypes = {
  viewMode: PropTypes.string,
  onDataChange: PropTypes.func,
  childId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  childName: PropTypes.string,
  therapistId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  therapistName: PropTypes.string
};

export default ParentTrainingMode;
